
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";

const FALLBACK =
    "https://cdn.rareblocks.xyz/collection/celebration/images/content/3/girl-working-on-laptop.jpg";

/* ── Dot indicator ── */
const Dot = ({ active, onClick }) => (
    <button
        onClick={onClick}
        aria-label="slide"
        style={{
            width: active ? "22px" : "8px",
            height: "8px",
            borderRadius: "99px",
            background: active ? "var(--flex-primary, #2563eb)" : "var(--flex-primary-muted, #bfdbfe)",
            border: "none",
            cursor: "pointer",
            transition: "all 0.3s",
            padding: 0,
        }}
    />
);
const formatTo12Hour = (timeStr) => {
    if (!timeStr) return "";
    const [h, m] = timeStr.split(":").map(Number);
    const date = new Date();
    date.setHours(h, m);
    return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
};
/* ── Open/Closed status helper ── */
const getOpenStatus = (schedule, timezone) => {
    try {
        const workingDays = schedule?.workingDays || [];
        const openTime = schedule?.openHours || "08:00";
        const closeTime = schedule?.closeHours || "18:00";
        const tz = timezone || "America/New_York";
        const now = new Date();

        const dayName = now.toLocaleDateString("en-US", { timeZone: tz, weekday: "long" });
        const isWorkingDay = workingDays.some(
            (d) => d?.toLowerCase() === dayName.toLowerCase()
        );
        if (!isWorkingDay) return { open: false, opensAt: null };

        const timeStr = now.toLocaleTimeString("en-US", {
            timeZone: tz,
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
        const [curH, curM] = timeStr.split(":").map(Number);
        const [openH, openM] = openTime.split(":").map(Number);
        const [closeH, closeM] = closeTime.split(":").map(Number);

        const cur = curH * 60 + curM;
        const open = openH * 60 + openM;
        const close = closeH * 60 + closeM;

        if (cur >= open && cur < close) return { open: true, opensAt: null };
        if (cur < open) return { open: false, opensAt: formatTo12Hour(openTime) };
        return { open: false, opensAt: null };
    } catch {
        return { open: false, opensAt: null };
    }
};

/* ── Open / Closed pill ── */
const OpenStatusBadge = ({ schedule, timezone }) => {
    const [status, setStatus] = useState(() => getOpenStatus(schedule, timezone));

    useEffect(() => {
        setStatus(getOpenStatus(schedule, timezone));
        const t = setInterval(
            () => setStatus(getOpenStatus(schedule, timezone)),
            60_000
        );
        return () => clearInterval(t);
    }, [schedule, timezone]);

    const isOpen = status.open;
    
    // Only show the badge if the business is open
    if (!isOpen) {
        return null; // Don't render anything when closed
    }
    
    const styles = {
        background: "rgba(22,163,74,0.15)",
        border: "1px solid rgba(22,163,74,0.5)",
        color: "#15803d",
    };
    
    return (
        <div
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 12px",
                borderRadius: "99px",
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "",
                background: "rgba(22,163,74,0.08)",
                border: "1px solid rgba(22,163,74,0.25)",
                color: "#16a34a",
                userSelect: "none",
                ...styles,
            }}
        >
            <span
                style={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: "#22c55e",
                    animation: "greenPulse 2s infinite",
                    boxShadow: "0 0 6px rgba(34,197,94,0.8)",
                    flexShrink: 0,
                }}
            />
            OPEN NOW
        </div>
    );
};

/* ── Arrow button ── */
const ArrowBtn = ({ direction, onClick }) => (
    <button
        onClick={onClick}
        aria-label={direction === "prev" ? "Previous slide" : "Next slide"}
        style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, var(--flex-primary, #2563eb), var(--flex-primary-hover, #1d4ed8))",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px var(--flex-primary-muted, rgba(37,99,235,0.3))",
            transition: "all 0.2s ease",
            flexShrink: 0,
        }}
        onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.1)";
            e.currentTarget.style.boxShadow = "0 6px 18px var(--flex-primary-muted, rgba(37,99,235,0.45))";
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 4px 12px var(--flex-primary-muted, rgba(37,99,235,0.3))";
        }}
    >
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth={2.5}>
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={direction === "prev" ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"}
            />
        </svg>
    </button>
);

const MarketingSection = () => {
    const [activeIdx, setActiveIdx] = useState(0);
    const [isHovering, setIsHovering] = useState(false);
    const [isFloating, setIsFloating] = useState(true);
    const company = useSelector((state) => state.companyReducer.company);
    const navigate = useNavigate();
    const intervalRef = useRef(null);
    const resumeTimerRef = useRef(null);

    /* ── schedule + timezone ── */
    const schedule = company?.workingSchedule || {};
    const timezone = company?.timezone || company?.operatingLocation?.zoneId || "America/New_York";

    /* ── same data wiring as original ── */
    const marketingImages =
        company?.contents?.filter(
            (c) =>
                c?.contentType === "IMAGE" &&
                c?.tags?.some((t) => t?.name === "Marketing Banner")
        ) || [];

    const displayImage =
        marketingImages.length > 0
            ? marketingImages[activeIdx]?.displayLink
            : FALLBACK;

    const activeSlideName =
        marketingImages.length > 0
            ? marketingImages[activeIdx]?.name || ""
            : "";

    /* ── Per-slide description: each image's own description,
            falls back to company description ── */
    const activeSlideDescription =
        marketingImages.length > 0
            ? marketingImages[activeIdx]?.description || company?.description || ""
            : company?.description || "";
    const cleanTitle = activeSlideName.replace(/<[^>]*>/g, "").trim();
    const cleanDescription = activeSlideDescription.replace(/<[^>]*>/g, "").trim();

    /* ── Schedule float resume after a delay ── */
    const scheduleResume = useCallback((delay = 1500) => {
        if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
        resumeTimerRef.current = setTimeout(() => {
            setIsFloating(true);
        }, delay);
    }, []);

    /* ── Cancel any pending resume (user is back) ── */
    const cancelResume = useCallback(() => {
        if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
        setIsFloating(false);
    }, []);

    /* ── Auto-rotate slides — pauses when hovering OR float is off ── */
    useEffect(() => {
        if (marketingImages.length <= 1 || !isFloating || isHovering) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            return;
        }
        intervalRef.current = setInterval(
            () => setActiveIdx((p) => (p + 1) % marketingImages.length),
            4000
        );
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [marketingImages.length, isFloating, isHovering]);

    /* ── Cleanup timers on unmount ── */
    useEffect(() => {
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
        };
    }, []);

    /* ── Called when user clicks/taps the card (not hover) ── */
    const handleUserInteraction = useCallback(() => {
        cancelResume();
        scheduleResume(5000);
    }, [cancelResume, scheduleResume]);

    /* ── Hover start: immediately pause everything ── */
    const handleHoverStart = useCallback(() => {
        cancelResume();
        setIsHovering(true);
        setIsFloating(false);
    }, [cancelResume]);

    /* ── Hover end: wait a short moment then smoothly resume ── */
    const handleHoverEnd = useCallback(() => {
        setIsHovering(false);
        scheduleResume(1200);
    }, [scheduleResume]);

    /* ── Touch events for mobile ── */
    const handleTouchStart = useCallback(() => {
        cancelResume();
        setIsHovering(true);
        setIsFloating(false);
    }, [cancelResume]);

    const handleTouchEnd = useCallback(() => {
        setIsHovering(false);
        scheduleResume(3000);
    }, [scheduleResume]);

    const handleSlideChange = useCallback((index) => {
        setActiveIdx(index);
        handleUserInteraction();
    }, [handleUserInteraction]);

    const handlePrev = useCallback(() => {
        setActiveIdx((p) => (p - 1 + marketingImages.length) % marketingImages.length);
        handleUserInteraction();
    }, [marketingImages.length, handleUserInteraction]);

    const handleNext = useCallback(() => {
        setActiveIdx((p) => (p + 1) % marketingImages.length);
        handleUserInteraction();
    }, [marketingImages.length, handleUserInteraction]);

    return (
        <section
            id="projects-section"
            style={{
                fontFamily: "var(--flex-font, 'Inter', sans-serif)",
                background: "var(--flex-bg, #f8faff)",
                backgroundImage:
                    "radial-gradient(circle, var(--flex-primary-muted, #bfdbfe) 1px, transparent 1px)",
                backgroundSize: "28px 28px",
                padding: "80px 0 110px",
                overflow: "hidden",
                position: "relative",
            }}
        >
            {/* soft blue ambient blobs */}
            <div
                aria-hidden
                style={{
                    position: "absolute",
                    top: "-100px",
                    right: "-100px",
                    width: "480px",
                    height: "480px",
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, var(--flex-primary-soft, rgba(37,99,235,0.08)) 0%, transparent 70%)",
                    pointerEvents: "none",
                }}
            />
            <div
                aria-hidden
                style={{
                    position: "absolute",
                    bottom: "-80px",
                    left: "-80px",
                    width: "360px",
                    height: "360px",
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, var(--flex-accent-soft, rgba(29,78,216,0.06)) 0%, transparent 70%)",
                    pointerEvents: "none",
                }}
            />

            <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px" }}>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                        gap: "64px",
                        alignItems: "center",
                    }}
                >
                    {/* ══ LEFT: hanging polaroid card ══════════════════════════════════════════════════ */}
                    <motion.div
                        initial={{ opacity: 0, y: -24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        viewport={{ once: true }}
                        style={{
                            position: "relative",
                            display: "flex",
                            justifyContent: "center",
                            paddingTop: "72px",
                        }}
                    >
                        {/* hanging wire */}
                        <div
                            aria-hidden
                            style={{
                                position: "absolute",
                                top: 0,
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: "2px",
                                height: "72px",
                                background:
                        "linear-gradient(to bottom, transparent 0%, var(--flex-accent, #93c5fd) 100%)",
                            }}
                        />
                        {/* pin */}
                        <div
                            aria-hidden
                            style={{
                                position: "absolute",
                                top: "-2px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                background: "linear-gradient(135deg, var(--flex-primary, #2563eb), var(--flex-primary-hover, #1d4ed8))",
                                boxShadow: "0 0 0 3px var(--flex-primary-soft, #dbeafe)",
                                zIndex: 2,
                            }}
                        />

                        {/* floating / swaying card */}
                        <motion.div
                            animate={
                                isFloating && !isHovering
                                    ? { y: [0, -8, 0], rotate: [-1.5, 1.5, -1.5] }
                                    : { y: 0, rotate: 0 }
                            }
                            transition={
                                isFloating && !isHovering
                                    ? { duration: 3, repeat: Infinity, ease: "easeInOut" }
                                    : { duration: 0.6, ease: "easeOut" }
                            }
                            onHoverStart={handleHoverStart}
                            onHoverEnd={handleHoverEnd}
                            onTouchStart={handleTouchStart}
                            onTouchEnd={handleTouchEnd}
                            style={{
                                background: "rgba(255,255,255,0.72)",
                                backdropFilter: "blur(18px)",
                                WebkitBackdropFilter: "blur(18px)",
                                borderRadius: "20px",
                                padding: "12px 12px 48px",
                                border: "1px solid rgba(255,255,255,0.55)",
                                boxShadow:
                                    "0 24px 64px var(--flex-primary-soft, rgba(37,99,235,0.14)), 0 4px 14px rgba(0,0,0,0.06)",
                                transformOrigin: "top center",
                                maxWidth: "420px",
                                width: "100%",
                                cursor: "default",
                                transition: "box-shadow 0.3s ease",
                            }}
                            whileHover={{
                                boxShadow: "0 32px 80px var(--flex-primary-soft, rgba(37,99,235,0.2))",
                            }}
                        >
                            {/* top accent line */}
                            <div
                                style={{
                                    height: "3px",
                                    borderRadius: "99px",
                                    background:
                                        "linear-gradient(90deg, var(--flex-primary, #2563eb), var(--flex-primary-hover, #1d4ed8), var(--flex-accent, #93c5fd))",
                                    marginBottom: "12px",
                                }}
                            />

                            <AnimatePresence mode="wait">
                                <motion.img
                                    key={displayImage}
                                    src={displayImage}
                                    alt="About"
                                    initial={{ opacity: 0, scale: 1.03 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.45 }}
                                    style={{
                                        display: "block",
                                        width: "100%",
                                        maxHeight: "520px",
                                        objectFit: "contain",
                                        background: "#fff",
                                        borderRadius: "12px",
                                        userSelect: "none",
                                        WebkitUserSelect: "none",
                                        cursor: "default",
                                    }}
                                    draggable={false}
                                />
                            </AnimatePresence>

                            {/* polaroid bottom: arrows + dots (multi) or company name (single) + open badge */}
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: "10px",
                                    paddingTop: "14px",
                                    minHeight: "28px",
                                }}
                            >
                                {/* slide controls row */}
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    {marketingImages.length > 1 ? (
                                        <>
                                            <ArrowBtn direction="prev" onClick={handlePrev} />
                                            {marketingImages.map((_, i) => (
                                                <Dot
                                                    key={i}
                                                    active={i === activeIdx}
                                                    onClick={() => handleSlideChange(i)}
                                                />
                                            ))}
                                            <ArrowBtn direction="next" onClick={handleNext} />
                                        </>
                                    ) : null}
                                </div>

                                {/* open / closed badge */}
                                <OpenStatusBadge schedule={schedule} timezone={timezone} />
                            </div>
                        </motion.div>

                        {/* floor shadow */}
                        <div
                            aria-hidden
                            style={{
                                position: "absolute",
                                bottom: "-20px",
                                left: "12%",
                                right: "12%",
                                height: "24px",
                                background:
                                    "radial-gradient(ellipse at center, var(--flex-primary-soft, rgba(37,99,235,0.15)) 0%, transparent 70%)",
                                filter: "blur(6px)",
                                pointerEvents: "none",
                            }}
                        />
                    </motion.div>

                    {/* ══ RIGHT: text ══════════════════════════════════════════════════════════════════ */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.65, delay: 0.15 }}
                        viewport={{ once: true }}
                        style={{
                            position: "sticky",
                            top: "120px",
                            alignSelf: "start",
                        }}
                    >
                        {/* badge */}
                        <motion.span
                            initial={{ opacity: 0, scale: 0.85 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.35, delay: 0.3 }}
                            viewport={{ once: true }}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "6px 16px",
                                borderRadius: "99px",
                                fontSize: "11px",
                                fontWeight: 700,
                                letterSpacing: "0.1em",
                                textTransform: "uppercase",
                                background: "linear-gradient(to right, var(--flex-primary-soft, #dbeafe), #f1f5f9)",
                                color: "var(--flex-primary, #2563eb)",
                                border: "1px solid var(--flex-primary-muted, #bfdbfe)",
                                marginBottom: "24px",
                            }}
                        >
                            <span
                                style={{
                                    width: "6px",
                                    height: "6px",
                                    borderRadius: "50%",
                                    background: "var(--flex-primary, #2563eb)",
                                    animation: "bluePulse 2s infinite",
                                }}
                            />
                            About Us
                        </motion.span>

                        {/* headline */}
                        <AnimatePresence mode="wait">
                            <motion.h2
                                key={activeIdx}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.35, ease: "easeOut" }}
                                id="projects-heading"
                                style={{
                                    fontSize: "clamp(2rem, 4vw, 3.25rem)",
                                    fontWeight: 800,
                                    lineHeight: 1.2,
                                    color: "#1e293b",
                                    marginBottom: "20px",
                                    letterSpacing: "-0.02em",
                                    whiteSpace: "pre-line",
                                }}
                            >
                                <span
                                    style={{
                                        background:
                                            "linear-gradient(90deg, var(--flex-primary, #2563eb) 0%, var(--flex-primary-hover, #1d4ed8) 100%)",
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                    }}
                                >
                                    {cleanTitle}
                                </span>
                            </motion.h2>
                        </AnimatePresence>

                        {/* description — fades + slides when slide changes */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeIdx}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.35, ease: "easeOut" }}
                                id="projects-description"
                                style={{
                                    fontSize: "1.05rem",
                                    lineHeight: 1.75,
                                    color: "#475569",
                                    marginBottom: "32px",
                                    maxWidth: "480px",
                                    maxHeight: "300px",
                                    overflowY: "auto",
                                    paddingRight: "12px",
                                    cursor: "text",
                                    userSelect: "text",
                                    WebkitUserSelect: "text",
                                }}
                                className="scrollable-text"
                            >
                                {cleanDescription}
                            </motion.div>
                        </AnimatePresence>

                        {/* feature pills */}
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "8px",
                                marginBottom: "36px",
                            }}
                        >
                            {[
                                "Flexible Spaces",
                                "Smart Booking",
                                "Community Events",
                                "Analytics",
                            ].map((chip) => (
                                <span
                                    key={chip}
                                    style={{
                                        padding: "5px 14px",
                                        borderRadius: "99px",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        background: "rgba(255,255,255,0.55)",
                                        backdropFilter: "blur(8px)",
                                        WebkitBackdropFilter: "blur(8px)",
                                        color: "var(--flex-primary, #2563eb)",
                                        border: "1px solid rgba(37,99,235,0.18)",
                                        cursor: "pointer",
                                        transition: "all 0.2s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = "var(--flex-primary, #2563eb)";
                                        e.currentTarget.style.color = "#fff";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = "var(--flex-primary-soft, #eff6ff)";
                                        e.currentTarget.style.color = "var(--flex-primary, #2563eb)";
                                    }}
                                >
                                    {chip}
                                </span>
                            ))}
                        </div>

                        {/* CTA button */}
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate("/our-features")}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "12px 32px",
                                borderRadius: "12px",
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                color: "#fff",
                                background: "linear-gradient(to right, var(--flex-primary, #2563eb), var(--flex-primary-hover, #1d4ed8))",
                                border: "none",
                                cursor: "pointer",
                                boxShadow: "0 8px 24px var(--flex-primary-muted, rgba(37,99,235,0.3))",
                                transition: "all 0.3s",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background =
                                    "linear-gradient(to right, var(--flex-primary-hover, #1d4ed8), var(--flex-primary-hover, #1e40af))";
                                e.currentTarget.style.boxShadow =
                                    "0 8px 28px var(--flex-primary-muted, rgba(37,99,235,0.45))";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background =
                                    "linear-gradient(to right, var(--flex-primary, #2563eb), var(--flex-primary-hover, #1d4ed8))";
                                e.currentTarget.style.boxShadow =
                                    "0 8px 24px var(--flex-primary-muted, rgba(37,99,235,0.3))";
                            }}
                        >
                            Learn More About {company?.name || "CoworkFlex"}
                            <svg
                                style={{ width: "15px", height: "15px" }}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2.5}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                                />
                            </svg>
                        </motion.button>
                    </motion.div>
                </div>
            </div>

            <style>{`
            @keyframes bluePulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.35; }
            }
            @keyframes greenPulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50%       { opacity: 0.5; transform: scale(1.4); }
            }

            .scrollable-text::-webkit-scrollbar { width: 4px; }
            .scrollable-text::-webkit-scrollbar-track {
            background: #e2e8f0;
            border-radius: 10px;
            }
            .scrollable-text::-webkit-scrollbar-thumb {
            background: var(--flex-primary, #2563eb);
            border-radius: 10px;
            }
            .scrollable-text::-webkit-scrollbar-thumb:hover {
            background: var(--flex-primary-hover, #1d4ed8);
            }

            #projects-section {
            user-select: text;
            -webkit-user-select: text;
            }
        `}</style>
        </section>
    );
};

export default MarketingSection;
